import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./BodhiAlbumCard.css";

// Sample the image's average color by drawing it down to 1x1 and reading the
// single resulting pixel. Used to tint the letterbox around non-square covers.
const sampleAverageColor = (img) => {
	try {
		const canvas = document.createElement("canvas");
		canvas.width = 1;
		canvas.height = 1;
		const ctx = canvas.getContext("2d", { willReadFrequently: true });
		ctx.drawImage(img, 0, 0, 1, 1);
		const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
		return a > 0 ? `rgb(${r}, ${g}, ${b})` : null;
	} catch {
		// Canvas is tainted (cover served without CORS headers) — skip tinting.
		return null;
	}
};

const PlayGlyph = () => (
	<svg width="18" height="18" viewBox="0 0 14 14" fill="currentColor">
		<title>Play</title>
		<path d="M4 2l8 5-8 5z" />
	</svg>
);

const PauseGlyph = () => (
	<svg width="16" height="16" viewBox="0 0 12 12" fill="currentColor">
		<title>Pause</title>
		<rect x="2" y="1.5" width="3" height="9" rx="0.5" />
		<rect x="7" y="1.5" width="3" height="9" rx="0.5" />
	</svg>
);

// Prefer the composed cover (with text); fall back to the text-free background art.
const resolveCover = (album) =>
	album?.cover_image_url ||
	album?.album_details?.cover_image_url ||
	album?.album_details?.cover_url ||
	album?.background_image_url ||
	album?.album_details?.background_image_url ||
	album?.album_details?.background_url ||
	null;

const resolveSubtitle = (album) => {
	const artist =
		album?.artist || album?.artist_name || album?.artist_details?.name;
	if (artist) return artist;
	const count = album?.tracks_count ?? album?.media_count;
	if (count !== undefined && count !== null) {
		return `${count} ${count === 1 ? "track" : "tracks"}`;
	}
	return "";
};

const BodhiAlbumCard = ({
	album,
	onClick,
	onPlay,
	isPlaying = false,
	isCurrent = false,
	size = "default",
	className = "",
}) => {
	const navigate = useNavigate();
	const cover = resolveCover(album);
	const title = album?.title || "Untitled album";
	const subtitle = resolveSubtitle(album);

	const [bg, setBg] = useState(null);
	const [corsBlocked, setCorsBlocked] = useState(false);

	const handleImgLoad = (e) => {
		if (corsBlocked) return;
		const color = sampleAverageColor(e.currentTarget);
		if (color) setBg(color);
	};

	const cls = [
		"bodhi-album",
		size === "xs" && "bodhi-album-xs",
		isCurrent && "bodhi-album-current",
		className,
	]
		.filter(Boolean)
		.join(" ");

	const handlePlayClick = (e) => {
		e.stopPropagation();
		onPlay?.(album);
	};

	const handleCardClick = () => {
		if (onClick) {
			onClick(album);
		} else if (album?.id) {
			navigate(`/album/${album.id}`);
		}
	};

	return (
		<div className={cls}>
			<div className="bodhi-album-cover">
				<button
					type="button"
					className="bodhi-album-surface"
					onClick={handleCardClick}
					aria-label={subtitle ? `${title} — ${subtitle}` : title}
				>
					<div
						className="bodhi-album-art"
						style={bg ? { background: bg } : undefined}
					>
						{cover ? (
							<img
								src={cover}
								alt={title}
								crossOrigin={corsBlocked ? undefined : "anonymous"}
								onLoad={handleImgLoad}
								onError={() => !corsBlocked && setCorsBlocked(true)}
							/>
						) : (
							<div className="bodhi-album-placeholder" aria-hidden="true" />
						)}
					</div>
				</button>
				{onPlay && (
					<button
						type="button"
						className={`bodhi-album-play ${
							isCurrent && isPlaying ? "bodhi-album-play-pinned" : ""
						}`}
						onClick={handlePlayClick}
						aria-label={isCurrent && isPlaying ? "Pause" : "Play"}
					>
						{isCurrent && isPlaying ? <PauseGlyph /> : <PlayGlyph />}
					</button>
				)}
			</div>
			<div className="bodhi-album-meta">
				<div className="bodhi-album-title">{title}</div>
				{subtitle && <div className="bodhi-album-sub">{subtitle}</div>}
			</div>
		</div>
	);
};

export default BodhiAlbumCard;
